import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BasicModal } from '@life-line-lab/components-react'
import { isNil } from 'lodash'
// antd
import { ArrowLeftOutlined } from '@ant-design/icons'
import { Row } from 'antd'
// shared
import { DetailsContainer } from 'Components/shared/details-container'
import ModalContent from 'Components/shared/modal-content'
import TabFooter from './tab-footer/tab-footer'
// hooks
import { useSession } from 'Hooks/index'
// types
import { AccountRole, DataListType, ModalTranslationContent, Statuses } from 'Types/common.types'
import { FetchedUserData } from 'Types/user.types'
// scss
import "./user-profile-two.scss"

interface UserProfileProps {
    canEdit:boolean,
    canDelete:boolean,
    userDetails:FetchedUserData;
    onBack:()=>void;
    onActivateUser:()=>void;
}

const UserProfileTwo:React.FC<UserProfileProps> = ({
    canEdit,
    canDelete,
    userDetails,
    onBack,
    onActivateUser,
}) => {

    const { t } = useTranslation();
    const { org } = useSession();
    const initialModal = {
        isOpen: false, record: {}, type: "approve"
    };

    /**
     * modify user data in {x}Info `DataListType` typed array
     * e.g. each object element represent a single column in `details-container`
     */
    const userInfo: DataListType[] = [
        {
            label: t("label.fullName"),
            value: userDetails.profile?.fullName ?? "-",
        },
        {
            label: t("label.passportID"),
            value: userDetails.profile?.idPassportNo ?? "-",
        },
        {
            label: t("label.mobileNo"),
            value: userDetails.profile?.mobileNo?.fullNumber ?? "-",
        },
        {
            label: t("label.email"),
            value: userDetails.profile?.email ?? "-",
        },
        {
            label: t("label.gender"),
            value: userDetails.profile?.gender ?? "-",
        },
        {
            label: t("label.homeAddress"),
            value: userDetails.profile?.fullAddress ?? "-",
        },
    ];

    const employmentInfo: DataListType[] = [
        {
            label: t("label.company").toUpperCase(),
            value: userDetails.org?.name ?? "-",
        },
        {
            label: t("label.location"),
            value: userDetails.locationName ?? "-",
        },
        {
            label: t("label.department"),
            value: userDetails.userRole?.department ?? "-",
        },
        {
            label: t("label.roleSubType"),
            value: userDetails.userRole?.subType ?? "-",
        },
    ];

    const paymentInfo: DataListType[] = [
        {
            label:t("label.bankType"),
            value:userDetails.userRole?.bankType ?? '-',
        },
        {
            label:t("label.accountNo"),
            value:userDetails.userRole?.accountNo ?? '-',
        },
        {
            label:t("label.socsoNo"),
            value:userDetails.userRole?.socsoNo ?? '-',
        },
        {
            label:t("label.epfNo"),
            value:userDetails.userRole?.epfNo ?? '-',
        },
        {
            label:t("label.incomeTax"),
            value:userDetails.userRole?.incomeTax ?? '-',
        },
        {
            label:t("label.hrdfNo"),
            value:userDetails.userRole?.hrdfNo ?? '-',
        },
        {
            label:t("label.zakatNo"),
            value:userDetails.userRole?.zakatNo ?? '-',
        },
    ];

    const emergencyInfo: DataListType[]  = [
        {
            label:t("label.name"),
            value:userDetails.userRole?.emergencyContactName ?? '-', 
        },
        {
            label:t("label.relationship"),
            value:userDetails.userRole?.relationship ?? '-',
        },
        {
            label:t("label.contactNo"),
            value:userDetails.userRole?.emergencyContactNo?.fullNumber ?? '-',
        },
    ];

    const attachmentInfo: DataListType[] = [
        {
            label:t("label.nricPassport").toUpperCase(),
            value:userDetails.profile?.nric_passport?.fileName ?? '-',
            link:!isNil(userDetails.profile?.nric_passport?.file_url) ? userDetails.profile?.nric_passport?.file_url : `#`
        },
    ];
    
    const [modal, setModal] = useState<any>(initialModal);

    useEffect(() => {
        if(userDetails.userRole?.isDeactivated?.tempDeletedReason){
            userInfo.push({
                label: t("label.reasonForDeactivate"),
                value: (userDetails.userRole.isDeactivated.tempDeletedReason).toString(),
                color:'red',
            },)
        }
    }, [userDetails])

    // ? write as a helper function
    const handleModalTranslation = () : ModalTranslationContent => {
        if (modal.type === Statuses.ACTIVATE) {
            return {
                title: t("label.activateUser").toUpperCase(),
                sureTxt: t("txt.areYouSureActivateAccount"),
                contentToConfirm: t("txt.userID"),
                actionLabel: t("action.activate")
            }
        } 
        return {
            title: '-',
            sureTxt: '-',
            contentToConfirm: '-',
            actionLabel: '-'
        }
    }

    const handleSetModal = (type:string) => {
        setModal({
            ...modal, isOpen:true, record:userDetails, type:type,
        })
    }

    const handleResetModalState = () => {
        setModal(initialModal);
    }

    return (
        <div className='deactivated-users-details-container'>
            <div className="my-10 main-header">
                <span className="cursor-pointer back-action"
                    onClick={() => { onBack() }}
                >
                    <ArrowLeftOutlined />
                    <span className="ml-5">{t("txt.back")}</span>
                </span>
                <span className="center header-title">
                    ID : {userDetails.userRole?.code} | {org.orgType === AccountRole.COMPANY ? t("title.companyUser").toUpperCase() : t("title.customerUser").toUpperCase()}
                </span>
            </div>

            <DetailsContainer
                listOfData={userInfo}
            />

            <div className="center my-10 header-title">
                {t("title.employmentDetails").toUpperCase()}
            </div>

            <DetailsContainer
                listOfData={employmentInfo}
            />

            <div className="center my-10 header-title">
                {t("title.paymentDetails").toUpperCase()}
            </div>

            <DetailsContainer
                listOfData={paymentInfo}
            />

            <div className="center my-10 header-title">
                {t("title.emergencyContacts").toUpperCase()}
            </div>

            <DetailsContainer
                listOfData={emergencyInfo}
            />

            <div className="center my-10 header-title">
                {t("title.attachments").toUpperCase()}
            </div>

            <DetailsContainer
                listOfData={attachmentInfo}
            />

            <Row justify='end'>
                <TabFooter
                    canEdit={canEdit}
                    canDelete={canDelete}
                    handleSetModal={handleSetModal}
                    userDetails={userDetails}
                />
            </Row>

            <BasicModal
                isOpen={modal.isOpen}
                isClosable={true}
                isMaskClosable={false}
                onClose={() => {handleResetModalState()}}
            >
                <ModalContent 
                    modal={modal} 
                    onHandleModalTranslation={handleModalTranslation}
                    onResetModal={handleResetModalState}
                    onActivate={()=>onActivateUser()}
                />
            </BasicModal>
        </div>
    )
}

export default UserProfileTwo